import { useEffect, useRef, type InputHTMLAttributes } from 'react';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'checked'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  'aria-label': string;
  /** Visible caption; omit for a bare box inside a table cell. */
  label?: string | undefined;
  indeterminate?: boolean | undefined;
  /** Let a long caption wrap over several lines instead of being clipped —
   *  for the facet lists, whose set names do not fit the filter column. */
  wrap?: boolean | undefined;
}

export function Checkbox({
  checked,
  onChange,
  label,
  indeterminate = false,
  wrap = false,
  className = '',
  ...rest
}: Props) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const input = (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={`h-3.5 w-3.5 shrink-0 accent-[var(--co-brand)] disabled:opacity-50 ${
        wrap ? 'mt-[3px]' : ''
      } ${className}`}
      {...rest}
    />
  );

  if (!label) return input;

  return (
    <label
      className={`flex min-w-0 gap-2 text-[12px] text-[var(--co-text)] ${
        wrap ? 'items-start' : 'items-center'
      }`}
    >
      {input}
      <span className={wrap ? 'break-words' : 'truncate'}>{label}</span>
    </label>
  );
}
